import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";

function ChapterSelector({
  books,
  currentBook,
  currentChapter,
  onBookChange,
  onChapterChange,
  textSize,
  onTextSizeChange,
  fontType,
  onFontTypeChange,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleBookChange = (event) => {
    const newBookNumber = event.target.value;
    const bookObj = books.find((b) => b.number === newBookNumber);
    if (!bookObj) return;
    const bookId = bookObj.name.replace(/ /g, "-");
    onBookChange(bookId);
    navigate(`/read/book/${bookId}/chapter/1`);
  };

  const handleChapterChange = (event) => {
    const newChapterNumber = event.target.value;
    onChapterChange(newChapterNumber);
    const bookId = currentBook ? currentBook.name.replace(/ /g, "-") : "";
    navigate(`/read/book/${bookId}/chapter/${newChapterNumber}`);
  };

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        p: { xs: 1.5, sm: 2 },
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: 2,
      }}
    >
      <FormControl
        fullWidth={isMobile}
        sx={{
          minWidth: isMobile ? "100%" : 200,
          mb: isMobile ? 1 : 0,
        }}
      >
        <InputLabel>Book</InputLabel>
        <Select
          value={currentBook ? currentBook.number : ""}
          label="Book"
          onChange={handleBookChange}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          {books.map((book) => (
            <MenuItem key={book.number} value={book.number}>
              {book.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        fullWidth={isMobile}
        sx={{
          minWidth: isMobile ? "100%" : 120,
        }}
      >
        <InputLabel>Chapter</InputLabel>
        <Select
          value={currentChapter}
          label="Chapter"
          onChange={handleChapterChange}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
              },
            },
          }}
        >
          {Array.from(
            {
              length: currentBook?.chapters.length || 0,
            },
            (_, i) => i + 1
          ).map((chapter) => (
            <MenuItem key={chapter} value={chapter}>
              {chapter}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Text size and font type controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          ml: isMobile ? 0 : 2,
        }}
      >
        <button
          style={{
            fontSize: 16,
            padding: "2px 8px",
            borderRadius: 4,
            border: "1px solid #ccc",
            background: "#fafafa",
            cursor: "pointer",
          }}
          onClick={() => onTextSizeChange(Math.max(12, textSize - 2))}
          aria-label="Decrease text size"
        >
          A-
        </button>
        <button
          style={{
            fontSize: 16,
            padding: "2px 8px",
            borderRadius: 4,
            border: "1px solid #ccc",
            background: "#fafafa",
            cursor: "pointer",
          }}
          onClick={() => onTextSizeChange(Math.min(32, textSize + 2))}
          aria-label="Increase text size"
        >
          A+
        </button>
        <FormControl size="small" sx={{ minWidth: 120, ml: 1 }}>
          <InputLabel>Font</InputLabel>
          <Select
            value={fontType}
            label="Font"
            onChange={(e) => onFontTypeChange(e.target.value)}
          >
            <MenuItem value="default">Default</MenuItem>
            <MenuItem value="opendyslexic">OpenDyslexic</MenuItem>
            <MenuItem value="serif">Serif</MenuItem>
            <MenuItem value="sans">Sans-serif</MenuItem>
            <MenuItem value="mono">Monospace</MenuItem>
            <MenuItem value="merriweather">Merriweather</MenuItem>
            <MenuItem value="lato">Lato</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}

export default ChapterSelector;
